import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import AdminBackButton from "@/components/AdminBackButton";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { findEquipmentConflicts, findCrossSectionConflicts } from "@/lib/equipmentConflicts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Clock, AlertTriangle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return Math.round((mins / 60) * 100) / 100;
}

const MINE_ACTIVITIES_FALLBACK = [
  "Stripping",
  "Reclamation",
  "Mining/Production",
  "Mine Maintenance/Development",
  "Pond Maintenance",
  "Plant Maintenance",
];

const EMPTY_FORM = {
  equipment_id: "",
  activity: "",
  start_time: "",
  end_time: "",
  operator_name: "",
  notes: "",
};

export default function EquipmentHoursPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [items, setItems] = useState([]);
  const [materialMovements, setMaterialMovements] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [activities, setActivities] = useState([]);
  const [report, setReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [entries, matMovements, lookups, r] = await Promise.all([
        api.get(`/reports/${reportId}/equipment-hours`),
        api.get(`/reports/${reportId}/material-movements`),
        api.get("/lookups"),
        api.get(`/reports/${reportId}`),
      ]);
      setItems(entries || []);
      setMaterialMovements(matMovements || []);
      setEquipment(lookups.equipment || []);
      setActivities(lookups.activities || []);
      setReport(r);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [reportId]);

  const isReadOnly = report?.status === "Submitted";
  const getEquipName = (id) => equipment.find(e => e.id === id)?.unit_id || "-";

  // Check for conflicts when form changes
  const conflicts = useMemo(() => {
    if (!form.equipment_id || !form.start_time || !form.end_time) return { same: [], cross: [] };
    
    const sameSection = findEquipmentConflicts(form, items, editItem?.id, equipment);
    const crossSection = findCrossSectionConflicts(form, items, materialMovements, 'equipment', editItem?.id, equipment);
    
    return { same: sameSection, cross: crossSection };
  }, [form, items, materialMovements, editItem, equipment]);

  const hasConflicts = conflicts.same.length > 0 || conflicts.cross.length > 0;

  const openAdd = () => {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      equipment_id: item.equipment_id || "",
      activity: item.activity || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      operator_name: item.operator_name || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.equipment_id || !form.start_time || !form.end_time) {
      toast.error("Equipment, start time, and end time are required");
      return;
    }
    
    // Show warning but allow save if there are conflicts
    if (hasConflicts) {
      toast.warning("Saving with time conflicts. Please verify the schedule.");
    }
    
    setSaving(true);
    const opHours = calcHours(form.start_time, form.end_time);
    const payload = { ...form, operating_hours: opHours };
    try {
      if (editItem) {
        await api.put(`/reports/${reportId}/equipment-hours/${editItem.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post(`/reports/${reportId}/equipment-hours`, payload);
        toast.success("Entry added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/${reportId}/equipment-hours/${id}`);
      toast.success("Entry deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const totalOp = items.reduce((s, i) => s + (Number(i.operating_hours) || 0), 0);

  // Find all conflicts for display in the table
  const itemConflicts = useMemo(() => {
    const conflictMap = {};
    items.forEach(item => {
      const others = items.filter(i => i.id !== item.id);
      const sameConflicts = findEquipmentConflicts(item, others, null, equipment);
      const crossConflicts = findCrossSectionConflicts(item, items, materialMovements, 'equipment', item.id, equipment);
      if (sameConflicts.length > 0 || crossConflicts.length > 0) {
        conflictMap[item.id] = { same: sameConflicts, cross: crossConflicts };
      }
    });
    return conflictMap;
  }, [items, materialMovements, equipment]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen" data-testid="equipment-hours-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <AdminBackButton />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" strokeWidth={1.5} /> Equipment Hours
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              <span className="kpi-value">{items.length}</span> entries
              <span className="mx-3 text-border">|</span>
              Total: <span className="kpi-value">{totalOp.toFixed(1)}h</span>
            </p>
          </div>
          {!isReadOnly && (
            <Button onClick={openAdd} data-testid="add-equipment-hours-button" className="font-headline font-semibold">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center text-muted-foreground text-sm">
              No equipment hours logged yet
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Equipment</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Activity</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Start</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">End</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Hours</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Operator</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Notes</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                  {!isReadOnly && <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const hasItemConflict = itemConflicts[item.id];
                  return (
                    <TableRow key={item.id} className={`border-border ${hasItemConflict ? 'bg-orange-500/5' : ''}`} data-testid={`equipment-row-${item.id}`}>
                      <TableCell className="font-medium text-sm">{getEquipName(item.equipment_id)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.activity || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{item.start_time || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">{item.end_time || "-"}</TableCell>
                      <TableCell className="kpi-value text-sm">{(Number(item.operating_hours) || 0).toFixed(1)}h</TableCell>
                      <TableCell className="text-sm">{item.operator_name || "-"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[150px]">{item.notes || "-"}</TableCell>
                      <TableCell>
                        {hasItemConflict ? (
                          <span className="inline-flex items-center gap-1 text-orange-400 text-xs" title={`Conflicts with ${hasItemConflict.same.length + hasItemConflict.cross.length} other entries`}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Conflict
                          </span>
                        ) : (
                          <span className="text-primary text-xs">OK</span>
                        )}
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border" data-testid="equipment-hours-dialog">
            <DialogHeader>
              <DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Equipment Hours</DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">Enter equipment operating time details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Equipment *</Label>
                <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                  <SelectTrigger data-testid="eq-equipment-select" className="bg-background border-border"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.unit_id} ({e.equipment_type})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Activity *</Label>
                <Select value={form.activity} onValueChange={(v) => setForm({ ...form, activity: v })}>
                  <SelectTrigger data-testid="eq-activity-select" className="bg-background border-border"><SelectValue placeholder="Select activity" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {(activities.length ? activities.map(a => a.name) : MINE_ACTIVITIES_FALLBACK).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Start Time *</Label>
                  <Input data-testid="eq-start-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-background border-border font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">End Time *</Label>
                  <Input data-testid="eq-end-time" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-background border-border font-mono" />
                </div>
              </div>
              
              {/* Conflict Warning */}
              {hasConflicts && (
                <Alert className="border-orange-500/50 bg-orange-500/10">
                  <AlertTriangle className="h-4 w-4 text-orange-400" />
                  <AlertDescription className="text-orange-300 text-sm">
                    <strong>Schedule Conflict Detected!</strong>
                    <ul className="mt-2 space-y-1 text-xs">
                      {conflicts.same.map((c, i) => (
                        <li key={`same-${i}`}>
                          <strong>{c.equipmentName}</strong> already scheduled in Equipment Hours: {c.existingTime}
                        </li>
                      ))}
                      {conflicts.cross.map((c, i) => (
                        <li key={`cross-${i}`}>
                          <strong>{c.equipmentName}</strong> used in {c.section}: {c.existingTime}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[10px] text-orange-400/70">You can still save, but please verify the schedule is correct.</p>
                  </AlertDescription>
                </Alert>
              )}
              
              {form.start_time && form.end_time && (
                <p className="text-sm text-muted-foreground">Calculated hours: <span className="kpi-value">{calcHours(form.start_time, form.end_time).toFixed(1)}h</span></p>
              )}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Operator Name</Label>
                <Input data-testid="eq-operator-name" value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} placeholder="Operator name" className="bg-background border-border" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Notes</Label>
                <Input data-testid="eq-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="bg-background border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-equipment-hours-button" className={`font-headline font-semibold ${hasConflicts ? 'bg-orange-500 hover:bg-orange-600' : ''}`}>
                {saving ? "Saving..." : hasConflicts ? "Save Anyway" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
